import type { DecisionLedgerAccountabilityDto, DecisionLedgerRecoveryExpectationDto,
  DecisionLedgerVerificationPlanWriteDto }
  from './decision-ledger-recovery';
export type { DecisionLedgerAccountabilityDto } from './decision-ledger-recovery';
const GOVERNED_SOURCE_DIGEST_ID = 'decision_ledger_schema' as const;
void GOVERNED_SOURCE_DIGEST_ID;
import type {
  ExecutionCockpitDailyBriefDto,
  ExecutionCockpitDecisionTargetsDto,
} from './execution-cockpit';
import type { HrisAtsCapacityActionKey } from './integrations/hris-ats-capacity-actions';
import type {
  PortfolioHiringCapacityWritebackOperationKey,
  PortfolioHiringCapacityWritebackTargetSystem,
} from './integrations/portfolio-hiring-capacity-writeback';
import type {
  InitiativeObservationBottleneckDto,
  InitiativeObservationStatusDto,
  InitiativePortfolioOperatingSnapshotV1,
  InitiativeRecommendationTypeDto,
} from './initiative-operating-intelligence';

export const RECOVERY_DECISION_TYPE = 'synergy_recovery_option';
export const DECISION_LEDGER_ACTIVATION_PATHS = {
  generic: 'generic', governed: 'governed',
} as const;
const GENERIC_DECISION = { activation: DECISION_LEDGER_ACTIVATION_PATHS.generic,
  synergy: false, legacyWeeklyLoop: false,
  genericVerificationSnapshots: true, legacySynergyPresentation: false } as const;
const WEEKLY_SYNERGY_DECISION = { ...GENERIC_DECISION, synergy: true,
  legacyWeeklyLoop: true, legacySynergyPresentation: true } as const;

type DecisionLedgerDecisionCapabilities = {
  decisionType: string;
  activation: typeof DECISION_LEDGER_ACTIVATION_PATHS[
    keyof typeof DECISION_LEDGER_ACTIVATION_PATHS];
  synergy: boolean; legacyWeeklyLoop: boolean; activationDeniedReason?: string;
  genericVerificationSnapshots: boolean; legacySynergyPresentation: boolean;
};

/** Recognition, activation and background enrollment are separate registry capabilities. */
export const DECISION_LEDGER_DECISION_REGISTRY = [
  { decisionType: 'blueprint_run_quote_to_cash', ...GENERIC_DECISION },
  { decisionType: 'blueprint_run_value_creation', ...GENERIC_DECISION },
  { decisionType: 'blueprint_operating_model', ...GENERIC_DECISION },
  { decisionType: 'blueprint_transformation', ...GENERIC_DECISION },
  { decisionType: 'blueprint_integration', ...GENERIC_DECISION },
  { decisionType: 'blueprint_turnaround', ...GENERIC_DECISION },
  { decisionType: 'blueprint_cost_transformation', ...GENERIC_DECISION },
  { decisionType: 'blueprint_growth', ...GENERIC_DECISION },
  { decisionType: 'execution_risk_memo', ...GENERIC_DECISION,
    activation: DECISION_LEDGER_ACTIVATION_PATHS.governed, activationDeniedReason:
      'Execution risk memo activation must flow through the governed ' +
      'decision package activation path.',
  },
  { decisionType: 'fusion_v2_integration_pack', ...GENERIC_DECISION },
  { decisionType: 'org_operating_model_decision', ...GENERIC_DECISION },
  { decisionType: 'portfolio_planner_plan', ...GENERIC_DECISION },
  { decisionType: 'initiative_operating_intelligence_case', ...GENERIC_DECISION },
  { decisionType: 'strategy_value_creation_decision', ...GENERIC_DECISION },
  { decisionType: 'strategy_pack_change', ...GENERIC_DECISION },
  { decisionType: 'strategic_execution_gap_decision', ...GENERIC_DECISION },
  { decisionType: 'portfolio_reallocation_decision', ...GENERIC_DECISION },
  { decisionType: 'workforce_reduction_program', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_logistics_network', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_esg_emissions', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_manufacturing_throughput', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_gtm_cac_payback', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_supplier_disruption_risk', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_retail_inventory_margin', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_bank_credit_risk_capital', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_energy_field_service_reliability', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_healthcare_access_capacity', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_insurance_claims_leakage', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_pharma_launch_readiness', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_ma_synergy_tracking', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_private_equity_value_creation', ...GENERIC_DECISION },
  { decisionType: 'decision_agent_inventory_turns', ...GENERIC_DECISION },
  { decisionType: 'synergy_recovery_date', ...WEEKLY_SYNERGY_DECISION },
  { decisionType: 'synergy_target_amendment', ...WEEKLY_SYNERGY_DECISION },
  { decisionType: 'synergy_dependency_plan', ...WEEKLY_SYNERGY_DECISION },
  { decisionType: 'synergy_claim_trace', ...WEEKLY_SYNERGY_DECISION },
  { decisionType: 'synergy_value_lever', ...WEEKLY_SYNERGY_DECISION },
  { decisionType: RECOVERY_DECISION_TYPE, ...WEEKLY_SYNERGY_DECISION, legacyWeeklyLoop: false,
    genericVerificationSnapshots: false, legacySynergyPresentation: false,
    activation: DECISION_LEDGER_ACTIVATION_PATHS.governed, activationDeniedReason:
      'Recovery option activation must flow through the governed ' +
      'evaluated verification-plan path.' },
] as const satisfies readonly DecisionLedgerDecisionCapabilities[];
export type DecisionLedgerDecisionRegistryEntry = typeof DECISION_LEDGER_DECISION_REGISTRY[number];
export type DecisionLedgerDecisionTypeDto = DecisionLedgerDecisionRegistryEntry['decisionType']
  | typeof import('./decision-ledger-saved-action').SAVED_ACTION_DECISION_TYPE
  | 'decision_agent_productivity_lean_ai'
  | 'decision_agent_segment_margin_cleanup'
  | 'decision_agent_professional_fees'
  | 'decision_agent_tech_software'
  | 'decision_agent_real_estate_footprint'
  | 'decision_agent_shared_services'
  | 'decision_agent_marketing_spend'
  | 'decision_agent_network_optimization'
  | 'decision_agent_capacity_utilization'
  | 'decision_agent_mode_mix_shift'
  | 'decision_agent_fuel_efficiency'
  | 'decision_agent_outside_contractors'
  | 'decision_agent_labor_arbitrage'
  | 'decision_agent_revenue_mix_shift'
  | 'decision_agent_growth_cross_sell'
  | 'decision_agent_churn_reduction'
  | 'decision_agent_working_capital'
  | 'decision_agent_capex_discipline'
  | 'decision_agent_semantic_metric_query';
export type DecisionLedgerSynergyDecisionTypeDto =
  Extract<DecisionLedgerDecisionRegistryEntry, { synergy: true }>['decisionType'];
export const DECISION_LEDGER_SUPPORTED_DECISION_TYPES =
  DECISION_LEDGER_DECISION_REGISTRY.map(entry => entry.decisionType);
export const DECISION_LEDGER_SYNERGY_DECISION_TYPES = DECISION_LEDGER_DECISION_REGISTRY
  .filter((entry): entry is Extract<DecisionLedgerDecisionRegistryEntry, { synergy: true }> =>
    entry.synergy).map(entry => entry.decisionType);
export const DECISION_LEDGER_LEGACY_WEEKLY_DECISION_TYPES = DECISION_LEDGER_DECISION_REGISTRY
  .filter(entry => entry.legacyWeeklyLoop).map(entry => entry.decisionType);

// Stable, lowercase slug. v1 examples: "strategy", "org_scenario", "fusion_integration".
export type DecisionLedgerScopeKindDto = string;

export type DecisionLedgerDecisionCadenceDto = 'weekly' | 'monthly' | 'quarterly' | 'ad_hoc';

export const DECISION_LEDGER_LEGACY_PRESENTATION_TYPES = DECISION_LEDGER_DECISION_REGISTRY
  .filter((entry): entry is Extract<DecisionLedgerDecisionRegistryEntry,
    { legacySynergyPresentation: true }> => entry.legacySynergyPresentation)
  .map(entry => entry.decisionType);

export type DecisionLedgerDecisionStatusDto = 'draft' | 'approved_unactivated' | 'active' | 'archived';

export type DecisionLedgerPackStatusDto = 'draft' | 'active' | 'superseded';

export type DecisionLedgerDecisionLifecycleActionDto = 'roll_forward_started' | 'reopened';

export type DecisionLedgerTimelineEventKindDto =
  | 'created'
  | 'decision_updated'
  | 'activated'
  | 'reviewed'
  | 'roll_forward_started'
  | 'reopened'
  | 'superseded'
  | 'settings_configured'
  | 'settings_updated';

export type DecisionLedgerDecisionLifecycleSummaryDto = {
  lastAction: DecisionLedgerDecisionLifecycleActionDto;
  packageId: string;
  actorUserId: string | null;
  createdAt: string; // ISO timestamp
};

export type DecisionLedgerDecisionModeDto = 'envelope' | 'selection';
export type DecisionLedgerMandateEnvelopeScopeDto = {
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
};
export type DecisionLedgerMandateEnvelopeNumericTargetDto = {
  metricKey: string;
  value: number;
  unit: string;
  definition: string;
  source: string;
  asOf: string;
  scope: DecisionLedgerMandateEnvelopeScopeDto;
  timeframe: string;
  reconciliationRule: string;
  evidenceRefIds: string[];
};

export type DecisionLedgerMandateEnvelopeConstraintDto = {
  metricKey: string;
  unit: string;
  min?: number;
  max?: number;
  value?: number;
  definition: string;
  source: string;
  asOf: string;
  scope: DecisionLedgerMandateEnvelopeScopeDto;
  timeframe: string;
  reconciliationRule: string;
  evidenceRefIds: string[];
};

export type DecisionLedgerMandateEnvelopeDto = {
  schemaVersion: 'decision_ledger_mandate_envelope.v1';
  target: DecisionLedgerMandateEnvelopeNumericTargetDto;
  constraints: DecisionLedgerMandateEnvelopeConstraintDto[];
};

export type DecisionLedgerDecisionDto = {
  decisionId: string;
  decisionType: DecisionLedgerDecisionTypeDto;
  strategyId: string;
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
  decisionMode?: DecisionLedgerDecisionModeDto | null;
  mandateEnvelope?: DecisionLedgerMandateEnvelopeDto | null;
  linkedEnvelopeDecisionId?: string | null;
  linkedSelectionDecisionId?: string | null;

  title: string;
  cadence: DecisionLedgerDecisionCadenceDto;
  ownerUserId: string | null;
  approverUserIds: string[];

  status: DecisionLedgerDecisionStatusDto;

  verificationPlanVersion: number;
  verificationPlan: DecisionLedgerVerificationPlanDto | null;
  verificationLatest: DecisionLedgerVerificationLatestDto | null;
  lifecycle: DecisionLedgerDecisionLifecycleSummaryDto | null;

  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
};

export type DecisionLedgerDecisionCreateRequestDto = {
  decisionType?: DecisionLedgerDecisionTypeDto;
  decisionMode?: DecisionLedgerDecisionModeDto | null;
  mandateEnvelope?: DecisionLedgerMandateEnvelopeDto | null;
  linkedEnvelopeDecisionId?: string | null;
  linkedSelectionDecisionId?: string | null;
};

export type DecisionLedgerDecisionUpdateRequestDto = {
  decisionType?: DecisionLedgerDecisionTypeDto;
  decisionMode?: DecisionLedgerDecisionModeDto | null;
  mandateEnvelope?: DecisionLedgerMandateEnvelopeDto | null;
  linkedEnvelopeDecisionId?: string | null;
  linkedSelectionDecisionId?: string | null;
  title?: string | null;
  cadence?: DecisionLedgerDecisionCadenceDto | null;
  ownerUserId?: string | null;
  approverUserIds?: string[] | null;
  status?: DecisionLedgerDecisionStatusDto | null;
};

export type DecisionLedgerDecisionLifecycleRequestDto = {
  decisionType: DecisionLedgerDecisionTypeDto;
  action: DecisionLedgerDecisionLifecycleActionDto;
  packageId: string;
};

export type DecisionLedgerGovernanceHistoryStatusDto = 'available' | 'unavailable';

export type DecisionLedgerTimelineItemDto = {
  eventId: string;
  kind: DecisionLedgerTimelineEventKindDto;
  actorUserId: string | null;
  createdAt: string; // ISO timestamp
};

export type DecisionLedgerEvidenceCoverageSummaryDto = {
  connected: number;
  stale: number;
  notConnected: number;
};

export type DecisionLedgerVerificationMetricSensitivityDto =
  | 'none'
  | 'finance'
  | 'people_pii'
  | 'people_highly_sensitive';
export type DecisionLedgerVerificationActualsSourceModeDto =
  | 'plan_only'
  | 'plan_plus_external'
  | 'external_only';

export type DecisionLedgerVerificationMetricSourceBindingDto = {
  kind: string;
  sourceArtifactId: string;
  sourceSystemRef: string;
  sourceContextKind: string;
  sourceSchemaVersion: string;
  sourceMetricPath: string;
  sourceAsOfPath: string;
  sourceFreshnessDays: number;
};

export const DECISION_LEDGER_REALIZATION_COMMITMENT_SCHEMA_VERSION =
  'decision_ledger_realization_commitment_v1';

export type DecisionLedgerRealizationCommitmentMetricDirectionDto =
  | 'increase'
  | 'decrease'
  | 'hold';

export type DecisionLedgerRealizationCommitmentMetricBindingDto = {
  metricKey: string;
  label: string;
  unit: string;
  direction: DecisionLedgerRealizationCommitmentMetricDirectionDto;
  definition: string;
  scope: string;
  source: string;
  sourceArtifactId: string;
  sourceMetricId: string;
  sensitivity: DecisionLedgerVerificationMetricSensitivityDto;
  reconciliationRule: string;
  baselineAsOf: string;
  warningThresholdRatio?: number | null;
  driftThresholdRatio?: number | null;
  amendmentThresholdRatio?: number | null;
};

export type DecisionLedgerRealizationCurvePointDto = {
  asOf: string;
  plannedValue: number;
  sourceArtifactId: string;
  sourceRef: string | null;
  timingProfile: string;
};

export type DecisionLedgerRealizationCommitmentValueBindingDto = {
  formulaId: string;
  formulaVersion: string;
  formulaRef: string;
  outputMetric: string;
  source: 'approved_formula_registry';
  asOf: string;
  sourceArtifactId: string;
};

export type DecisionLedgerRealizationCommitmentOwnerDto = {
  ownerUserId: string | null;
  ownerPosition?: Readonly<{ orgStateId: string; positionId: string }>;
  ownerLabel: string;
  source: string;
};
export type DecisionLedgerRealizationCommitmentPropagationBindingDto = {
  packageInitiativeKey: string;
  source:
    | 'strategy_autopilot_lever'
    | 'ma_synergy_lever'
    | 'workforce_reduction_lever';
};

export type DecisionLedgerRealizationCommitmentGateRefDto = {
  finalizationPackageId: string;
  finalizationPackageVersion: number;
  decisionLedgerDecisionId: string;
  orgScenarioId: string;
  toBeOrgStateId: string;
  activatedAt: string;
  activatedByUserId: string;
  receiptKind:
    | 'rif_program_finalization_package_activation'
    | 'strategy_autopilot_draft_approval'
    | 'ma_fusion_scenario_activation';
};

export type DecisionLedgerRealizationCommitmentProposalMetadataDto = {
  source: 'strategy_autopilot' | 'ma_fusion_scenario_activation';
  draftArtifactId: string;
  intentText: string;
  sourceCitation: {
    page: number;
    section: string;
  };
  watchStreams: string[];
  watchMapStatus: 'proposal_pending_binding';
  approvedAt: string;
};

export type DecisionLedgerRealizationCommitmentDto = {
  schemaVersion: typeof DECISION_LEDGER_REALIZATION_COMMITMENT_SCHEMA_VERSION;
  decisionId: string;
  leverId: string;
  metricBinding: DecisionLedgerRealizationCommitmentMetricBindingDto;
  baselineValue: number;
  targetValue: number;
  targetDate: string;
  realizationCurve: DecisionLedgerRealizationCurvePointDto[];
  valueBinding: DecisionLedgerRealizationCommitmentValueBindingDto;
  owner: DecisionLedgerRealizationCommitmentOwnerDto;
  propagationBinding?: DecisionLedgerRealizationCommitmentPropagationBindingDto | null;
  gateRef: DecisionLedgerRealizationCommitmentGateRefDto;
  proposalMetadata?: DecisionLedgerRealizationCommitmentProposalMetadataDto;
};

export type DecisionLedgerVerificationPlanMetricDto = {
  metricKey: string;
  label: string;
  unit: string;
  direction: 'increase' | 'decrease';
  sensitivity: DecisionLedgerVerificationMetricSensitivityDto;
  definition?: string | null;
  scope?: string | null;
  source?: string | null;
  sourceBinding?: DecisionLedgerVerificationMetricSourceBindingDto | null;
  sourceKind?: string | null;
  sourceSystemRef?: string | null;
  sourceContextKind?: string | null;
  sourceSchemaVersion?: string | null;
  sourceMetricPath?: string | null;
  sourceAsOfPath?: string | null;
  sourceFreshnessDays?: number | null;
  reconciliationRule?: string | null;
  baselineValue: number | null;
  targetValue: number | null;
  targetAt: string | null; // ISO timestamp
  warningThresholdRatio?: number | null;
  driftThresholdRatio?: number | null;
  amendmentThresholdRatio?: number | null;
};

export type DecisionLedgerVerificationPlanDto = {
  baselineAsOf: string;
  metrics: DecisionLedgerVerificationPlanMetricDto[];
  accountability?: DecisionLedgerAccountabilityDto | null;
  recoveryExpectation?: DecisionLedgerRecoveryExpectationDto | null;
  recoveryVerification?: import('./decision-ledger-recovery').DecisionLedgerRecoveryVerificationDto;
};

export type DecisionLedgerVerificationLatestMetricDto = {
  metricKey: string;
  label: string;
  unit: string;
  sensitivity: DecisionLedgerVerificationMetricSensitivityDto;
  definition?: string | null;
  scope?: string | null;
  source?: string | null;
  reconciliationRule?: string | null;
  actualValue: number | null;
  expectedValue: number | null;
  targetValue: number | null;
  targetAt: string | null; // ISO timestamp
  driftStatus: 'on_track' | 'watch' | 'drifting' | 'amendment_required' | 'unknown';
  redacted?: boolean;
};

export type DecisionLedgerVerificationMetricRealizationDto = {
  commitmentId: string;
  commitmentCurveVersion: string | null;
  expectedValuePolicy: 'realization_commitment_curve';
  projectedMissUsd: number;
  formulaId: string;
  formulaVersion: string;
};

export type DecisionLedgerVerificationMetricWithRealizationDto =
  DecisionLedgerVerificationLatestMetricDto & {
    realization?: DecisionLedgerVerificationMetricRealizationDto | null;
  };

export type DecisionLedgerVerificationLatestSummaryDto = {
  tracked: number;
  onTrack: number;
  watch: number;
  drifting: number;
  amendmentRequired: number;
  unknown: number;
  redacted: number;
};

export type DecisionLedgerVerificationEvidenceDto = {
  // Evidence references are IDs only (no raw payloads). Full artifacts live outside the Decision Ledger.
  observedOrgStateId?: string | null;
  observedExecutionSnapshotId?: string | null;
  observedExecutionSnapshotArtifactId?: string | null;
  observedExecutionReconciliationArtifactId?: string | null;
  sourceArtifactRefs?: string[] | null;
  actualsSourceMode?: DecisionLedgerVerificationActualsSourceModeDto | null;
  actualsSummary?: string | null;
};

export type DecisionLedgerVerificationLatestDto = {
  asOf: string; // ISO timestamp
  summary: DecisionLedgerVerificationLatestSummaryDto;
  metrics: DecisionLedgerVerificationMetricWithRealizationDto[];
  evidence?: DecisionLedgerVerificationEvidenceDto | null;
};

export type DecisionLedgerVerificationSnapshotDto = {
  snapshotId: string;
  planVersion: number;
  recordedAt: string; // ISO timestamp
  asOf: string; // ISO timestamp
  summary: DecisionLedgerVerificationLatestSummaryDto;
  metrics: DecisionLedgerVerificationLatestMetricDto[];
  evidence?: DecisionLedgerVerificationEvidenceDto | null;
};

export type DecisionLedgerVerificationSnapshotsListResponseDto = {
  snapshots: DecisionLedgerVerificationSnapshotDto[];
};

export type DecisionLedgerVerificationPlanUpsertRequestDto = DecisionLedgerVerificationPlanWriteDto;

export type DecisionLedgerVerificationSnapshotMetricActualDto = {
  metricKey: string;
  actualValue: number | null;
  expectedValue?: number | null;
  targetValue?: number | null;
  driftStatus?: 'on_track' | 'watch' | 'drifting' | 'amendment_required' | 'unknown';
  realization?: DecisionLedgerVerificationMetricRealizationDto | null;
};

export type DecisionLedgerVerificationSnapshotRecordRequestDto = {
  asOf: string; // ISO timestamp
  actuals: DecisionLedgerVerificationSnapshotMetricActualDto[];
  evidence?: DecisionLedgerVerificationEvidenceDto | null;
};

export type DecisionLedgerVerificationRagDto = 'green' | 'amber' | 'red';

export type DecisionLedgerVerificationMetricDto = {
  rag: DecisionLedgerVerificationRagDto | null;
  slipWeeks: number | null;
  gapPoints: number | null;
  stalledCount: number | null;
};

export type DecisionLedgerVerificationInitiativeDto = {
  initiativeKey: string;
  baseline: DecisionLedgerVerificationMetricDto;
  expectedTargets: ExecutionCockpitDecisionTargetsDto | null;
  current: DecisionLedgerVerificationMetricDto | null;
  delta: {
    slipWeeks: number | null;
    gapPoints: number | null;
    stalledCount: number | null;
  } | null;
  driftStatus: 'on_track' | 'watch' | 'drifting' | 'amendment_required' | 'unknown';
};

export type DecisionLedgerVerificationSummaryDto = {
  tracked: number;
  onTrack: number;
  watch: number;
  drifting: number;
  amendmentRequired: number;
  unknown: number;
};

export type DecisionLedgerVerificationDto = {
  packageId: string;
  activatedAt: string; // ISO timestamp
  baselineAsOf: string;
  currentRunId: string | null;
  currentAsOf: string | null; // ISO timestamp
  summary: DecisionLedgerVerificationSummaryDto;
  initiatives: DecisionLedgerVerificationInitiativeDto[];
};

export type DecisionLedgerPortfolioPackageDecisionTestDto = {
  decideNow: string;
  whyNow: string;
  ifLeadershipWaits: string;
  whatIsDrifting: string;
  weakestEvidence: string;
  enterNext: string;
};

export type DecisionLedgerPortfolioPackageSelectionSummaryDto = {
  selectedInitiativeCount: number;
  deferredInitiativeCount: number;
};

export type DecisionLedgerPortfolioPackageConstraintContextDto = {
  capabilityId: string | null;
  label: string;
  demandHours: number | null;
  supplyHours: number | null;
  gapHours: number | null;
  shadowPriceUsdPerHour: number | null;
};

export type DecisionLedgerPortfolioPackageStrategyPillarDto = {
  strategyId: string;
  title: string;
  description: string | null;
};

export type DecisionLedgerPortfolioPackageStrategyContextDto = {
  anchorStrategyId: string | null;
  strategyPackVersionId: string | null;
  pillarCount: number;
  pillars: DecisionLedgerPortfolioPackageStrategyPillarDto[];
  alignedSelectedInitiativeCount: number;
  alignedDeferredInitiativeCount: number;
  unlinkedSelectedInitiativeCount: number;
  unlinkedDeferredInitiativeCount: number;
  summary: string;
};

export type DecisionLedgerPortfolioPackageActualsFreshnessDto = {
  freshestAsOf: string | null;
  stalestAsOf: string | null;
  staleSourceCount: number;
  missingTimestampCount: number;
  evidenceBasis: {
    governedSourceCount: number;
    timestampedSourceCount: number;
    okSourceCount: number;
    warningSourceCount: number;
    errorSourceCount: number;
    summary: string;
  };
  coverageWindow: {
    earliestAsOf: string | null;
    latestAsOf: string | null;
    spanDays: number | null;
    summary: string;
  };
  assumptions: string[];
  summary: string;
  weakestSource: {
    source: string;
    status: string;
    asOf: string | null;
    dataGap: string | null;
  } | null;
};

export type DecisionLedgerPortfolioPackageCalibrationDto = {
  publishedPlanCount: number;
  cycleCount: number;
  p50ForecastMaeWeeks: number | null;
  p50ForecastMedianErrorWeeks: number | null;
  selectionStabilityAvg: number | null;
  portfolioHealthAvg: number | null;
  averageOptimizerValueAddUsd: number | null;
  measuredUpliftPct: number | null;
  externalActuals: {
    checkpointCountWithExternalActuals: number;
    cycleCountWithExternalActuals: number;
    latestCycleAsOf: string | null;
    latestCheckpointAsOf: string | null;
    latestExecutionProvider: string | null;
    latestCoverageAsOf: string | null;
    latestCoverageExecutionProvider: string | null;
    latestOrgCapacityCoveragePct: number | null;
    latestExecutionInitiativeLinkageCoveragePct: number | null;
    latestExecutionAssigneeCoveragePct: number | null;
    orgCapacityCoverageCheckpointCount: number;
    executionInitiativeLinkageCoverageCheckpointCount: number;
    executionAssigneeCoverageCheckpointCount: number;
    averageOrgCapacityCoveragePct: number | null;
    averageExecutionInitiativeLinkageCoveragePct: number | null;
    averageExecutionAssigneeCoveragePct: number | null;
    latestExecutionWorkItemsTotal: number | null;
    latestExecutionOpenWorkItemsTotal: number | null;
    latestExecutionClosedWorkItemsTotal: number | null;
    coverageUnknownReasons: string[];
    assumptions: string[];
    summary: string;
  };
  evidenceBasis: {
    plansUsedCount: number;
    totalPublishedPlanCount: number;
    cycleCount: number;
    historyBounded: boolean;
    summary: string;
  };
  coverageWindow: {
    earliestPublishedAt: string | null;
    latestPublishedAt: string | null;
    earliestPlanAsOf: string | null;
    latestPlanAsOf: string | null;
    spanDays: number | null;
    summary: string;
  };
  assumptions: string[];
  summary: string;
};

export type DecisionLedgerPortfolioPackageExecutionTruthSourceDto = {
  source: string;
  status: string;
  asOf: string | null;
  snapshotId: string | null;
  snapshotAsOf: string | null;
  snapshotInputsHash: string | null;
  reconciliationArtifactId: string | null;
  reconciliationInputsHash: string | null;
  workItemsTotal: number | null;
  openWorkItemsTotal: number | null;
  closedWorkItemsTotal: number | null;
  approvedScopeWorkItemsTotal: number | null;
  approvedScopeOpenWorkItemsTotal: number | null;
  approvedScopeClosedWorkItemsTotal: number | null;
  offScopeKnownInitiativeWorkItemsTotal: number | null;
  unknownStrategyInitiativeWorkItemsTotal: number | null;
  unattributedWorkItemsTotal: number | null;
  missingAssigneeWorkItemsTotal: number | null;
  reconciliationDataGaps: string[];
  summary: string;
};

export type DecisionLedgerPortfolioPackageExecutionTruthDto = {
  summary: string;
  weakestSourceSummary: string | null;
  sources: DecisionLedgerPortfolioPackageExecutionTruthSourceDto[];
};

export type DecisionLedgerPortfolioPackageCommitmentMemoryReviewStatusDto =
  | 'missing_governance'
  | 'missing_checkpoint_plan'
  | 'awaiting_first_checkpoint'
  | 'current'
  | 'overdue'
  | 'drifting';

export type DecisionLedgerPortfolioPackageCommitmentMemoryDto = {
  summary: string;
  ownerSummary: string;
  cadenceSummary: string;
  checkpointSummary: string;
  driftSummary: string;
  packageVersion: number;
  commitmentPackageId: string;
  activatedAt: string | null;
  decisionCreatedAt: string | null;
  ownerUserId: string | null;
  approverUserIds: string[];
  cadence: DecisionLedgerDecisionCadenceDto | null;
  status: DecisionLedgerDecisionStatusDto | null;
  verificationPlanVersion: number;
  baselineAsOf: string | null;
  nextReviewDueAt: string | null;
  reviewStatus: DecisionLedgerPortfolioPackageCommitmentMemoryReviewStatusDto;
  latestCheckpointAt: string | null;
  latestCheckpointSummary: DecisionLedgerVerificationLatestSummaryDto | null;
  verificationActualsSourceMode: DecisionLedgerVerificationActualsSourceModeDto | null;
  verificationActualsSummary: string | null;
  snapshotCount: number;
  hardFailReasons: string[];
};

export type DecisionLedgerPortfolioPackageScopedActualsFinanceStatusDto =
  | 'missing'
  | 'partial'
  | 'scoped_non_labor';

export type DecisionLedgerPortfolioPackageScopedActualsCurrencyTotalDto = {
  currency: string;
  amount: string;
};

export type DecisionLedgerPortfolioPackageScopedActualsDto = {
  summary: string;
  financeSummary: string;
  laborSummary: string;
  finance: {
    status: DecisionLedgerPortfolioPackageScopedActualsFinanceStatusDto;
    asOf: string | null;
    accountingArtifactId: string | null;
    scopeMappingArtifactId: string | null;
    expensesTotal: number | null;
    matchedExpensesTotal: number | null;
    ambiguousExpensesTotal: number | null;
    unmatchedExpensesTotal: number | null;
    matchedInitiativeCount: number | null;
    matchedTotalsByCurrency: DecisionLedgerPortfolioPackageScopedActualsCurrencyTotalDto[];
    dataGaps: string[];
  };
  labor: {
    mode: 'modeled_only';
    status: 'modeled_only';
    summary: string;
    unsupportedReason: string;
    modeledSources: string[];
    availableActualSources: string[];
    requiredArtifactKinds: string[];
  };
};

export type DecisionLedgerPortfolioPackageFinancialPostureModeledStatusDto =
  | 'missing'
  | 'partial'
  | 'complete';

export type DecisionLedgerPortfolioPackageFinancialPostureHistoryPointDto = {
  planId: string;
  packageVersion: number | null;
  publishedAt: string;
  asOf: string | null;
  selectedInitiativeCount: number;
  modeledDeliverySpendUsdP50: number | null;
  modeledDeliverySpendUsdP80: number | null;
  missingModeledFinanceInitiativeCount: number;
};

export type DecisionLedgerPortfolioPackageFinancialPostureDto = {
  summary: string;
  modeledEnvelopeSummary: string;
  realizedSummary: string;
  historySummary: string;
  modeled: {
    status: DecisionLedgerPortfolioPackageFinancialPostureModeledStatusDto;
    selectedInitiativeCount: number;
    modeledInitiativeCount: number;
    modeledDeliverySpendUsdP50: number | null;
    modeledDeliverySpendUsdP80: number | null;
    missingModeledFinanceInitiativeCount: number;
  };
  realized: {
    financeStatus: DecisionLedgerPortfolioPackageScopedActualsFinanceStatusDto;
    financeAsOf: string | null;
    matchedInitiativeCount: number | null;
    matchedTotalsByCurrency: DecisionLedgerPortfolioPackageScopedActualsCurrencyTotalDto[];
    laborMode: 'modeled_only';
  };
  history: DecisionLedgerPortfolioPackageFinancialPostureHistoryPointDto[];
};

export type DecisionLedgerPortfolioPackageFinancialControlMetricDto = {
  metricKey: string;
  label: string;
  unit: string;
  driftStatus: 'on_track' | 'watch' | 'drifting' | 'amendment_required' | 'unknown';
  actualValue: number | null;
  expectedValue: number | null;
  targetValue: number | null;
  targetAt: string | null;
  source: string | null;
  reconciliationRule: string | null;
};

export type DecisionLedgerPortfolioPackageFinancialControlDto = {
  summary: string;
  monitoredSummary: string;
  checkpointSummary: string;
  coverageSummary: string;
  escalationSummary: string;
  metricCount: number;
  driftingMetricCount: number;
  unknownMetricCount: number;
  actualsSourceMode: DecisionLedgerVerificationActualsSourceModeDto | null;
  actualsSummary: string | null;
  latestCheckpointAt: string | null;
  nextReviewDueAt: string | null;
  realizedFinanceStatus: DecisionLedgerPortfolioPackageScopedActualsFinanceStatusDto;
  realizedFinanceAsOf: string | null;
  laborMode: 'modeled_only';
  hardFailReasons: string[];
  metrics: DecisionLedgerPortfolioPackageFinancialControlMetricDto[];
};

export type DecisionLedgerPortfolioPackageCheckpointFinancialStateStatusDto =
  | 'unsupported'
  | 'bounded_partial'
  | 'scoped_non_labor_only';

export type DecisionLedgerPortfolioPackageCheckpointEnvelopeFloorStatusDto =
  | 'unsupported'
  | 'partial_floor'
  | 'scoped_non_labor_floor';

export type DecisionLedgerPortfolioPackageCheckpointEnvelopePressureStatusDto =
  | 'unsupported'
  | 'bounded_headroom'
  | 'p50_certain_breach'
  | 'p80_certain_breach'
  | 'p80_and_p50_certain_breach';

export type DecisionLedgerPortfolioPackageCheckpointFinancialStateDto = {
  summary: string;
  status: DecisionLedgerPortfolioPackageCheckpointFinancialStateStatusDto;
  baselineSummary: string;
  varianceSummary: string;
  envelopeFloorSummary: string;
  envelopeFloorStatus: DecisionLedgerPortfolioPackageCheckpointEnvelopeFloorStatusDto;
  pressureSummary: string;
  pressureStatus: DecisionLedgerPortfolioPackageCheckpointEnvelopePressureStatusDto;
  unsupportedReason: string | null;
  baselinePackageId: string | null;
  baselinePackageVersion: number | null;
  baselineAsOf: string | null;
  checkpointSummary: string;
  coverageSummary: string;
  boundarySummary: string;
  reviewSummary: string;
  actualsSourceMode: DecisionLedgerVerificationActualsSourceModeDto | null;
  actualsSummary: string | null;
  checkpointAsOf: string | null;
  scopedFinanceAsOf: string | null;
  matchedInitiativeCount: number | null;
  matchedTotalsByCurrency: DecisionLedgerPortfolioPackageScopedActualsCurrencyTotalDto[];
  modeledDeliverySpendUsdP50: number | null;
  modeledDeliverySpendUsdP80: number | null;
  scopedNonLaborSpendUsd: number | null;
  knownSpendFloorVarianceUsdP50: number | null;
  knownSpendFloorVarianceUsdP80: number | null;
  realizedFloorPctOfModeledP50: number | null;
  realizedFloorPctOfModeledP80: number | null;
  modeledHeadroomCeilingUsdP50: number | null;
  modeledHeadroomCeilingUsdP80: number | null;
  certainEnvelopeBreachUsdP50: number | null;
  certainEnvelopeBreachUsdP80: number | null;
  laborMode: 'modeled_only';
  hardFailReasons: string[];
  dataGaps: string[];
  accountingArtifactId: string | null;
  scopeMappingArtifactId: string | null;
};

export type DecisionLedgerPortfolioPackageCheckpointFollowThroughStatusDto =
  | 'blocked'
  | 'bounded'
  | 'attention'
  | 'on_track';

export type DecisionLedgerPortfolioPackageCheckpointFollowThroughDto = {
  summary: string;
  status: DecisionLedgerPortfolioPackageCheckpointFollowThroughStatusDto;
  checkpointSummary: string;
  financeRealizationSummary: string;
  executionRealizationSummary: string;
  boundarySummary: string;
  decisionPressureSummary: string;
  nextReviewSummary: string;
  checkpointAsOf: string | null;
  scopedFinanceAsOf: string | null;
  executionAsOf: string | null;
  hardFailReasons: string[];
  dataGaps: string[];
};

export type DecisionLedgerPortfolioPackageControlPlanStatusDto =
  | 'blocked'
  | 'attention'
  | 'on_track';

export type DecisionLedgerPortfolioPackageControlPlanDto = {
  summary: string;
  status: DecisionLedgerPortfolioPackageControlPlanStatusDto;
  ownerSummary: string;
  reviewSummary: string;
  triggerSummary: string;
  weakestEvidenceSummary: string;
  escalationSummary: string;
  nextSurfaceLabel: string;
  nextSurfaceReason: string;
  ownerUserId: string | null;
  nextReviewDueAt: string | null;
  latestCheckpointAt: string | null;
  hardFailReasons: string[];
};

export type DecisionLedgerPortfolioPackageHiringCapacityActionStatusDto =
  | 'ok'
  | 'warning'
  | 'error';

export type DecisionLedgerPortfolioPackageHiringCapacityActionDto = {
  summary: string;
  status: DecisionLedgerPortfolioPackageHiringCapacityActionStatusDto;
  headline: string;
  recommendation: string;
  recommendedActionKey: HrisAtsCapacityActionKey | null;
  recommendedActionTitle: string | null;
  recommendedActionSummary: string | null;
  whyThisMatters: string[];
  tradeoffs: string[];
  assumptions: string[];
  dataGaps: string[];
  targetCapabilityIds: string[];
  alternativeActionCount: number;
  actions: Array<{
    key: HrisAtsCapacityActionKey;
    status: 'recommended' | 'alternative';
    title: string;
    summary: string;
    targetCapabilityIds: string[];
    tradeoffs: string[];
  }>;
  evidence: {
    source: 'portfolio_hiring_capacity';
    sourceStatus: 'OK' | 'WARNING' | 'ERROR' | 'MISSING';
    asOf: string | null;
    atsSnapshotId: string | null;
    finchSnapshotId: string | null;
    demandCapabilityCount: number | null;
    overlapCapabilityCount: number | null;
    capabilityOverlapPct: number | null;
    openReqsWithoutHrisCapacityCount: number | null;
    positionsWithCapacityMissingPrimaryCapabilityCount: number | null;
  };
};

export type DecisionLedgerPortfolioPackageHiringCapacityWritebackPlanStatusDto =
  | 'ready'
  | 'blocked'
  | 'no_action';

export type DecisionLedgerPortfolioPackageHiringCapacityWritebackPlanDto = {
  summary: string;
  status: DecisionLedgerPortfolioPackageHiringCapacityWritebackPlanStatusDto;
  headline: string;
  recommendedActionKey: HrisAtsCapacityActionKey | null;
  recommendedActionTitle: string | null;
  recommendedActionSummary: string | null;
  targetSystems: PortfolioHiringCapacityWritebackTargetSystem[];
  operationCount: number;
  activation: {
    decisionType: 'portfolio_planner_plan';
    scopeKind: 'portfolio_scope';
    scopeId: string | null;
    mode: 'governed_dry_run';
    externalWritesEnabled: false;
    explicitApprovalRequired: true;
  };
  operations: Array<{
    key: PortfolioHiringCapacityWritebackOperationKey;
    targetSystem: PortfolioHiringCapacityWritebackTargetSystem;
    title: string;
    summary: string;
    targetCapabilityIds: string[];
    blockingReasons: string[];
  }>;
  guardrails: string[];
  assumptions: string[];
  dataGaps: string[];
  evidence: {
    source: 'portfolio_hiring_capacity';
    sourceStatus: 'OK' | 'WARNING' | 'ERROR' | 'MISSING';
    asOf: string | null;
    atsSnapshotId: string | null;
    finchSnapshotId: string | null;
    demandCapabilityCount: number | null;
    overlapCapabilityCount: number | null;
    capabilityOverlapPct: number | null;
    openReqsWithoutHrisCapacityCount: number | null;
    positionsWithCapacityMissingPrimaryCapabilityCount: number | null;
  };
};

export type DecisionLedgerPortfolioPackageHiringCapacityNextActionBundleStatusDto =
  | 'ready'
  | 'blocked'
  | 'no_action';

export type DecisionLedgerPortfolioPackageHiringCapacityNextActionBundleDto = {
  bundleId: string;
  executionRequestId: string;
  executionResultId: string;
  summary: string;
  status: DecisionLedgerPortfolioPackageHiringCapacityNextActionBundleStatusDto;
  headline: string;
  recommendedActionKey: HrisAtsCapacityActionKey | null;
  targetSystems: PortfolioHiringCapacityWritebackTargetSystem[];
  operationCount: number;
  totalItemCount: number;
  weakestDataGap: string | null;
  sourceArtifacts: {
    atsSnapshotId: string | null;
    finchSnapshotId: string | null;
  };
  guardrails: string[];
  assumptions: string[];
  dataGaps: string[];
  targets: {
    mergeAts: {
      status: 'ready' | 'blocked';
      artifactId: string | null;
      artifactAsOf: string | null;
      itemCount: number;
      openings: Array<{
        openingId: string;
        remoteKey: string | null;
        title: string;
        capabilityId: string;
        openedAt: string | null;
        filledAt: string | null;
        status: 'open';
        stageSummary: string;
      }>;
      dataGaps: string[];
    } | null;
    finchHris: {
      status: 'blocked';
      itemCount: number;
      dataGaps: string[];
    } | null;
    portfolioPlanner: {
      status: 'blocked';
      itemCount: number;
      dataGaps: string[];
    } | null;
  };
};

export type DecisionLedgerInitiativeOperatingPackageFocusItemDto = {
  initiativeId: string;
  status: InitiativeObservationStatusDto;
  bottleneck: InitiativeObservationBottleneckDto | 'missing_observation';
  primaryRecommendation: InitiativeRecommendationTypeDto | null;
  mappingConfidence: number | null;
  entityCoveragePct: number | null;
  evidenceGaps: string[];
};

export type DecisionLedgerInitiativeOperatingPackageDto = {
  packageId: string;
  summary: string;
  status: InitiativeObservationStatusDto;
  headline: string;
  whyThisMatters: string;
  recommendation: string;
  totalInitiatives: number;
  statusCounts: InitiativePortfolioOperatingSnapshotV1['statusCounts'];
  coverage: InitiativePortfolioOperatingSnapshotV1['coverage'];
  focusInitiatives: DecisionLedgerInitiativeOperatingPackageFocusItemDto[];
  dataGaps: string[];
};

export type DecisionLedgerPortfolioPackageDto = {
  planId: string;
  scopeRootNodeId: string;
  asOf: string | null;
  kernelVersion: string | null;
  inputsHash: string | null;
  sourceHash: string | null;
  executiveSummary: string | null;
  recommendation: string | null;
  tradeoffSummary: string | null;
  roiCase: string | null;
  confidenceCaveats: string | null;
  topActions: string[];
  decisionTest: DecisionLedgerPortfolioPackageDecisionTestDto;
  portfolioHealth: {
    score: number | null;
    summary: string | null;
  };
  selectionSummary: DecisionLedgerPortfolioPackageSelectionSummaryDto;
  constraintContext: DecisionLedgerPortfolioPackageConstraintContextDto;
  strategyContext: DecisionLedgerPortfolioPackageStrategyContextDto;
  topRecommendation: {
    capabilityId: string | null;
    unlockedValueUsd: number | null;
    roic: number | null;
  } | null;
  deferredInitiatives: Array<{
    initiativeId: string;
    narrativeSummary: string | null;
  }>;
  readiness: {
    overallStatus: string | null;
    publishBlocked: boolean;
    publishBlockerKeys: string[];
  };
  integrationHealth: {
    okCount: number;
    warningCount: number;
    errorCount: number;
    connected: number;
    stale: number;
    notConnected: number;
    sources: Array<{
      source: string;
      status: string;
      asOf: string | null;
      dataGap: string | null;
    }>;
  };
  actualsFreshness: DecisionLedgerPortfolioPackageActualsFreshnessDto;
  executionTruth: DecisionLedgerPortfolioPackageExecutionTruthDto | null;
  hiringCapacityAction?: DecisionLedgerPortfolioPackageHiringCapacityActionDto | null;
  hiringCapacityWritebackPlan?: DecisionLedgerPortfolioPackageHiringCapacityWritebackPlanDto | null;
  hiringCapacityNextActionBundle?: DecisionLedgerPortfolioPackageHiringCapacityNextActionBundleDto | null;
  commitmentMemory?: DecisionLedgerPortfolioPackageCommitmentMemoryDto | null;
  scopedActuals?: DecisionLedgerPortfolioPackageScopedActualsDto | null;
  financialPosture?: DecisionLedgerPortfolioPackageFinancialPostureDto | null;
  financialControl?: DecisionLedgerPortfolioPackageFinancialControlDto | null;
  checkpointFinancialState?: DecisionLedgerPortfolioPackageCheckpointFinancialStateDto | null;
  checkpointFollowThrough?: DecisionLedgerPortfolioPackageCheckpointFollowThroughDto | null;
  controlPlan?: DecisionLedgerPortfolioPackageControlPlanDto | null;
  calibration: DecisionLedgerPortfolioPackageCalibrationDto;
  governance: {
    status: DecisionLedgerDecisionStatusDto | null;
    ownerUserId: string | null;
    cadence: DecisionLedgerDecisionCadenceDto | null;
    approverUserIds: string[];
    decisionCreatedAt: string | null;
    verificationPlanVersion: number;
    latestCheckpointAt: string | null;
    latestCheckpointSummary: DecisionLedgerVerificationLatestSummaryDto | null;
    verificationActualsSourceMode: DecisionLedgerVerificationActualsSourceModeDto | null;
    verificationActualsSummary: string | null;
    nextReviewDueAt: string | null;
    snapshotCount: number;
    hardFailReasons: string[];
  };
  consistencyWarnings: string[];
  dataGaps: string[];
};

export type DecisionLedgerListItemDto = {
  decisionType: DecisionLedgerDecisionTypeDto;

  strategyId: string;
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
  runId: string;
  packageId: string;
  version: number;
  status: DecisionLedgerPackStatusDto;

  computedAt: string; // ISO timestamp
  asOf: string | null; // ISO timestamp, if available

  headline: string | null;

  // The accountable user on the governed record for this pack: the activation /
  // publish actor of the listed version (activatedByUserId ?? createdByUserId for
  // cockpit packages, publishedByUserId for portfolio plans). Null when the
  // governed record carries no actor — the UI renders that as a named gap, never
  // as an invented owner. (#4008 SFIX defect 3.)
  ownerUserId: string | null;

  createdAt: string; // ISO timestamp
  activatedAt: string | null; // ISO timestamp
};

export type DecisionLedgerReviewQueueStatusDto =
  | 'missing_governance'
  | 'missing_checkpoint_plan'
  | 'awaiting_first_checkpoint'
  | 'unknown'
  | 'current'
  | 'overdue'
  | 'drifting';

export type DecisionLedgerReviewQueueActivePackageLinkStatusDto =
  | 'linked'
  | 'unlinked'
  | 'ambiguous'
  | 'invalid_scope';

export type DecisionLedgerReviewQueueItemDto = {
  decisionType: DecisionLedgerDecisionTypeDto;
  strategyId: string;
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
  activePackageId: string | null;
  activePackageLinkStatus: DecisionLedgerReviewQueueActivePackageLinkStatusDto;
  title: string;
  decisionStatus: DecisionLedgerDecisionStatusDto;
  ownerUserId: string | null;
  approverUserIds: string[];
  cadence: DecisionLedgerDecisionCadenceDto | null;
  verificationPlanVersion: number;
  baselineAsOf: string | null;
  latestCheckpointAt: string | null;
  nextReviewDueAt: string | null;
  reviewStatus: DecisionLedgerReviewQueueStatusDto;
  driftingMetricCount: number;
  unknownMetricCount: number;
  redactedMetricCount: number;
  hardFailReasons: string[];
  summary: string;
  createdAt: string;
  updatedAt: string;
};

export type DecisionLedgerReviewQueueResponseDto = {
  items: DecisionLedgerReviewQueueItemDto[];
};

export type DecisionLedgerReviewTriggerWorkloadDto = 'weekly' | 'monthly';

export type DecisionLedgerReviewTriggerReasonCodeDto =
  | 'drift_detected'
  | 'structure_changed'
  | 'review_overdue'
  | 'review_due_this_cycle';

export type DecisionLedgerReviewTriggerStructureChangedDto = {
  positionsAdded: number;
  positionsRemoved: number;
  reportingLineChanges: number;
  directReportSpanChanges: number;
  monitoringHref: string;
};

export type DecisionLedgerReviewTriggerItemDto = DecisionLedgerReviewQueueItemDto & {
  triggerReasonCodes: DecisionLedgerReviewTriggerReasonCodeDto[];
  triggerSummary: string;
  structureChanged: DecisionLedgerReviewTriggerStructureChangedDto | null;
};

export type DecisionLedgerReviewTriggerResponseDto = {
  workloadCadence: DecisionLedgerReviewTriggerWorkloadDto;
  asOf: string;
  windowEndAt: string;
  items: DecisionLedgerReviewTriggerItemDto[];
};

export type DecisionLedgerMemoOpeningResponseDto = {
  workloadCadence: DecisionLedgerReviewTriggerWorkloadDto;
  asOf: string;
  windowEndAt: string;
  shouldOpenMemo: boolean;
  openingReasonCodes: DecisionLedgerReviewTriggerReasonCodeDto[];
  openingSummary: string;
  decisionType: DecisionLedgerDecisionTypeDto;
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
  triggerCount: number;
  items: DecisionLedgerReviewTriggerItemDto[];
};

export type DecisionLedgerBoardRollupSummaryDto = {
  activeDecisionCount: number;
  driftingDecisionCount: number;
  overdueDecisionCount: number;
  currentDecisionCount: number;
  unownedDecisionCount: number;
};

export type DecisionLedgerBoardRollupFocusItemDto = {
  key: string;
  decisionType: DecisionLedgerDecisionTypeDto;
  reviewStatus: DecisionLedgerReviewQueueStatusDto;
  reviewStatusLabel: string;
  scopeKind: DecisionLedgerScopeKindDto;
  scopeId: string;
  scopeLabel: string;
  title: string;
  affectedEntityLabel: string | null;
  metricLabel: string | null;
  metricComparison: string | null;
  materialitySummary: string | null;
  situation: string;
  whyNow: string;
  whyItMatters: string;
  boardAsk: string;
  ownerUserId: string | null;
  ownerGap: string | null;
  reviewTiming: string;
  evidenceItems: string[];
  gapItems: string[];
  actionItems: string[];
};

export type DecisionLedgerBoardRollupResponseDto = {
  strategyId: string;
  strategyLabel: string | null;
  workloadCadence: 'quarterly';
  asOf: string;
  headline: string;
  whyThisMatters: string;
  summary: DecisionLedgerBoardRollupSummaryDto;
  boardActionItems: DecisionLedgerBoardRollupFocusItemDto[];
  onTrackItems: DecisionLedgerBoardRollupFocusItemDto[];
  evidenceAndGapItems: string[];
  decisions: DecisionLedgerReviewQueueItemDto[];
};
export type DecisionLedgerDetailDto = {
  item: DecisionLedgerListItemDto;
  decision?: DecisionLedgerDecisionDto | null;

  provenance: {
    inputsHash: string;
    sourceKind: string;
    sourceRef: string | null;
    schemaVersion: string;
    engineVersion: string;
  } | null;

  // Deterministic, proof-backed summary for the pack (Phase 1: Execution Cockpit).
  dailyBrief: ExecutionCockpitDailyBriefDto | null;

  // Phase 1.5: Measurement loop for activated packs (baseline → targets → actuals).
  verification: DecisionLedgerVerificationDto | null;

  // Phase 2: Backend-normalized PP decision package payload.
  portfolioPackage?: DecisionLedgerPortfolioPackageDto | null;
  initiativeOperatingPackage?: DecisionLedgerInitiativeOperatingPackageDto | null;

  boardPackMd: string | null;
  evidenceCoverageSummary: DecisionLedgerEvidenceCoverageSummaryDto | null;
  dataGaps?: string[];
  governanceHistoryStatus?: DecisionLedgerGovernanceHistoryStatusDto;
  governanceHistoryReason?: string | null;
  timeline: DecisionLedgerTimelineItemDto[];
};
